'use strict';

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { useMealPlanData, useMealOperations, usePermissions, useUIState, useModalControls, useAssets } from '../contexts';
import RecipeToPdfButton from '@components/Recipes/RecipeToPdfButton.react';
import MultiSharePopup from '../menus/MultiSharePopup.react';
import SwapMenu from '../menus/SwapMenu.react';
import Popup from '@components/Widgets/Popup.react';
import RecommendPopup from '@components/Widgets/RecommendPopup.react';

import './ControlBar.scss';

/**
 * ControlBar - Action toolbar for MealDetails using el-toolbar1 framework
 *
 * Implements the standard meal detail actions (Print, Share, Add, Swap, etc.)
 * with responsive mobile/desktop layouts and context-aware functionality.
 */
const ControlBar = ({
    meals,
    offset,
    mealType,
    primary,
    dishIndex,
    sortedMeals,
    hidePrintBtn = false
}) => {
    // Context hooks
    const { plan } = useMealPlanData();
    const { startAddMeal, startReplaceMeal } = useMealOperations();
    const { canWrite, canCreate, isPro, inhibitSwap } = usePermissions();
    const { isMobile } = useUIState();
    const { recommendRecipe } = useModalControls();
    const { recipes } = useAssets();

    // Event handlers for toolbar actions

    const handleAdd = (ev) => {
        ev?.stopPropagation();
        if (startAddMeal) {
            startAddMeal(offset, mealType.name);
        }
    };

    // Get recipes from meals for recommendation
    const recipesInThisMeal = meals
        ?.map(meal => meal.recipe_uuid ? recipes[meal.recipe_uuid] : null)
        ?.filter(recipe => recipe) || [];


    return (
        <div className={classNames(
            'control-bar-container',
            'el-toolbar1',
            !isMobile && 'el-toolbar1-dark'
        )}>
            {/* Print Button */}
            {!hidePrintBtn && primary.recipe && (
                <RecipeToPdfButton
                    className="el-toolbar-popup"
                    positionClassName="el-popup-top-left"
                    button={
                        <>
                            {!isMobile && <i className="feather feather-printer" />}
                            <em>PRINT</em>
                        </>
                    }
                    recipe={primary.recipe}
                    meal={primary.primary}
                    plan={plan}
                />
            )}

            {/* Share Button */}
            <MultiSharePopup
                className="el-toolbar-popup"
                meals={meals}
                positionClassName="el-popup-top-center"
                buttonText="SHARE"
            />

            {/* Add Button */}
            {!inhibitSwap && (
                <button
                    className="el-toolbar-btn add-btn"
                    onClick={handleAdd}
                    title="Add meal"
                >
                    {!isMobile && <i className="feather feather-plus" />}
                    <em>ADD</em>
                </button>
            )}

            {/* Swap Button/Menu - Conditional based on meal count (allow virtual plans if user can create) */}
            {(canWrite || canCreate) && (
                meals.length === 1 ? (
                    <SwapMenu 
                        className="el-toolbar-btn" 
                        primary={primary.primary} 
                        meals={meals}
                        button={
                            <>
                                {!isMobile && <i className="icon-swap" />}
                                <em>SWAP</em>
                            </>
                        }
                    />
                ) : (
                    <Popup
                        positionClassName="el-popup-top-right"
                        className="el-toolbar-popup swap-popup"
                        button={
                            <>
                                {!isMobile && <i className="icon-swap" />}
                                <em>SWAP</em>
                            </>
                        }
                    >
                        <h6>Swap</h6>
                        <SwapMenu 
                            className="swap-popup-btn" 
                            primary={primary.primary} 
                            meals={meals}
                            button={<>Entire Meal</>} 
                        />
                        
                        {meals.map((meal, i) => (
                            <SwapMenu
                                key={i}
                                className="swap-popup-btn"
                                primary={meal}
                                meals={[meal]}
                                options={{ replace_single: true }}
                                button={
                                    <>
                                        DISH {i + 1}: <em>{primary.titles?.[i] || `Dish ${i + 1}`}</em>
                                    </>
                                }
                            />
                        ))}
                    </Popup>
                )
            )}

            {/* Recommend Button */}
            {isPro && recipesInThisMeal.length > 0 && (
                <RecommendPopup
                    className="el-toolbar-popup"
                    positionClassName="el-popup-top-right"
                    button={
                        <>
                            {!isMobile && <i className="feather feather-heart" />}
                            <em>RECOMMEND</em>
                        </>
                    }
                    recipes={recipesInThisMeal}
                    onClickItem={(recipeUuid) => {
                        const recipe = recipesInThisMeal.find(r => r.uuid === recipeUuid);
                        if (recipe) {
                            recommendRecipe(recipe);
                        }
                    }}
                />
            )}

        </div>
    );
};

ControlBar.propTypes = {
    meals: PropTypes.array.isRequired,
    offset: PropTypes.number.isRequired,
    mealType: PropTypes.shape({
        name: PropTypes.string.isRequired,
    }).isRequired,
    primary: PropTypes.object.isRequired,
    dishIndex: PropTypes.number.isRequired,
    sortedMeals: PropTypes.array.isRequired,
    hidePrintBtn: PropTypes.bool,
};

export default ControlBar;
